import { memo } from "react";
import { formatRelativeTime } from "../lib/format";
import type { FavoriteItem, HotItem, Hotlist, Platform } from "../types";
import { Icon } from "./Icon";
import { PlatformLogo } from "./PlatformLogo";

interface HotlistCardProps {
  platform: Platform;
  hotlist: Hotlist | undefined;
  favoriteKeys: Set<string>;
  onOpenItem: (item: HotItem) => void;
  onToggleFavorite: (item: HotItem) => void;
}

const statusLabels = {
  healthy: "正常",
  stale: "使用缓存",
  unavailable: "暂不可用",
  collecting: "采集中",
};

export const HotlistCard = memo(function HotlistCard({
  platform,
  hotlist,
  favoriteKeys,
  onOpenItem,
  onToggleFavorite,
}: HotlistCardProps) {
  const status = hotlist?.status || "unavailable";
  const items = hotlist?.items || [];

  return (
    <article className="hotlist-card">
      <header className="card-heading">
        <a href={platform.homepage} rel="noreferrer" target="_blank">
          <PlatformLogo platform={platform} />
          <strong>{platform.name}</strong>
        </a>
        <span className={`source-status status-${status}`}>{statusLabels[status]}</span>
      </header>

      {items.length ? (
        <ol className="hot-items">
          {items.slice(0, 10).map((item) => {
            const favorite = favoriteKeys.has(item.fingerprint);
            return (
              <li key={item.id}>
                <span className={`item-rank rank-${Math.min(item.rank, 4)}`}>{item.rank}</span>
                <a
                  href={item.url}
                  onClick={() => onOpenItem(item)}
                  rel="noreferrer"
                  target="_blank"
                >
                  <span className="item-title">{item.title}</span>
                  {item.heatLabel ? <span className="item-heat">{item.heatLabel}</span> : null}
                </a>
                <button
                  aria-label={favorite ? `取消收藏：${item.title}` : `收藏：${item.title}`}
                  className={`favorite-button ${favorite ? "is-active" : ""}`}
                  onClick={() => onToggleFavorite(item)}
                  type="button"
                >
                  <Icon name="bookmark" />
                </button>
              </li>
            );
          })}
        </ol>
      ) : (
        <div className="card-empty">
          <Icon name={status === "collecting" ? "refresh" : "alert"} />
          <strong>{status === "collecting" ? "正在采集" : "暂时没有可显示的数据"}</strong>
          <p>{hotlist?.error ? "公开数据源当前无法访问，请稍后刷新重试。" : "轻量接口完成首次采集后会自动显示。"}</p>
          <a href={platform.homepage} rel="noreferrer" target="_blank">前往原平台</a>
        </div>
      )}

      <footer className="card-footer">
        <span>{formatRelativeTime(hotlist?.updatedAt)}</span>
        <a href={platform.homepage} rel="noreferrer" target="_blank" aria-label={`打开${platform.name}`}>
          <Icon name="external" />
        </a>
      </footer>
    </article>
  );
});
